import React, { useState } from "react";
import axios from "axios";
import { X, KeyRound, Loader2, BadgeCheck } from "lucide-react";
import { API, authHeader } from "./adminApi";

const ChangePasswordModal = ({ onClose, onSuccess }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!current || !next) {
      setError("Please fill in both password fields.");
      return;
    }
    if (next.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeader() }
      );
      setDone(true);
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-change-pwd-modal"
      >
        <div className="relative bg-gray-900 px-6 py-5">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 mb-2">
            <KeyRound className="w-3 h-3" />
            Security
          </div>
          <h2 className="text-2xl font-light text-white tracking-tight">Change password</h2>
          <p className="text-sm text-white/70 font-light mt-1.5">
            You will be signed out after updating.
          </p>
        </div>

        <div className="p-6 sm:p-7">
          {done ? (
            <div className="text-center py-6" data-testid="admin-change-pwd-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <BadgeCheck className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Password updated</h3>
              <p className="text-sm text-gray-600 font-light">Signing you out…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <PwdField label="Current password" value={current} onChange={setCurrent} testid="admin-current-password" />
              <div>
                <PwdField label="New password" value={next} onChange={setNext} testid="admin-new-password" />
                <p className="text-[10px] text-gray-400 mt-1 font-light">Minimum 6 characters.</p>
              </div>
              <PwdField label="Confirm new password" value={confirm} onChange={setConfirm} testid="admin-confirm-password" />

              {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="admin-change-pwd-error">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900 px-3 py-2">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-60"
                  data-testid="admin-change-pwd-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating…
                    </>
                  ) : (
                    "Update password"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const PwdField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">
      {label}
    </label>
    <input
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="new-password"
      className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-light"
      data-testid={testid}
    />
  </div>
);

export default ChangePasswordModal;
